import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { getEmployeeHistory as fetchEmployeeHistoryApi } from "../../api/employees";
import { convertLeaveDay } from "../../api/leave";
import ConvertAttendanceToPermitModal from "./ConvertAttendanceToPermitModal";
import {
  attendanceStatusLabel,
  buildEmployeeDailyLedger,
  canHrConvertAttendanceToPermit,
  canHrConvertLeaveOrAbsenceDay,
  canViewEmployeeHistory,
  currentYearMonth,
  formatHistoryDate,
  formatHistoryTime,
  getEmployeeHistory,
  getModuleLabel,
  HR_CONVERT_LEAVE_TYPES,
  hrConvertDayTargets,
  leaveTypeLabel,
  monthBounds,
  overtimeDispositionLabel,
  requestStatusLabel,
} from "../../utils/employeeHistoryHelpers";
import { canViewSickLeaveRemaining, isHrFamilyRole } from "../../utils/hrStaffHelpers";
import { getEmployeeLeaveRemaining } from "../../utils/teamRequestsHelpers";
import { getAppealLevelLabel } from "../../utils/appealCycle";
import { LeaveRemainingValue } from "../manager/TeamLeaveRemaining";
import {
  formatPenaltyDeduction,
  formatPenaltyReason,
} from "../../utils/dataMappers";
import { formatPenaltyDays } from "../../utils/penaltyFormat";
import AppealSubjectDetails from "../common/AppealSubjectDetails";
import EmployeeDocumentsPanel from "../hr/EmployeeDocumentsPanel";
import EmployeeAgenda from "../manager/EmployeeAgenda";
import { ProfileReadOnlyFields } from "../employee/MyProfile";
import {
  completenessTone,
  fieldCompleteness,
  formatProfileMissingSummary,
} from "../../utils/profileCompleteness";
import "../employee/MyProfile.css";
import "../hr/HrProfileCompletion.css";
import "./EmployeeHistoryView.css";

const BASE_TABS = [
  { id: "daily", label: "الحضور اليومي" },
  { id: "timeline", label: "الخط الزمني" },
  { id: "requests", label: "الطلبات" },
  { id: "penalties", label: "الجزاءات" },
  { id: "agenda", label: "أجندة أبيض وأسود" },
  { id: "appeals", label: "التظلمات" },
  { id: "complaints", label: "الشكاوى" },
  { id: "resignations", label: "الاستقالات" },
];

const PROFILE_TAB = { id: "profile", label: "بيانات الملف" };
const DOCUMENTS_TAB = { id: "documents", label: "المستندات" };

function isUuid(value) {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

function mapApiTimeline(timeline = []) {
  return timeline.map((event, index) => ({
    id: `api-${index}-${event.sourceId || event.action}`,
    module: event.module,
    action: event.action,
    timestamp: event.timestamp,
    summary: event.summary,
    status: event.notes || null,
    meta: event,
  }));
}

function mapApiHistory(apiData) {
  if (!apiData) return null;

  const profile = apiData.profile || {};
  const timeline = mapApiTimeline(apiData.timeline);

  const leaveAsRequests = (apiData.leave || []).map((leave) => ({
    id: leave.id,
    type: leaveTypeLabel(leave.leaveType),
    submittedAt: leave.createdAt,
    startDate: leave.startDate,
    endDate: leave.endDate,
    reason: leave.reason,
    status: requestStatusLabel(leave.status),
  }));

  const permitAsRequests = (apiData.permits || []).map((permit) => ({
    id: permit.id,
    type: permit.isCompensatory ? "إذن تعويضي" : "إذن",
    submittedAt: permit.createdAt,
    permitDate: permit.permitDate,
    startTime: permit.startTime,
    endTime: permit.endTime,
    reason: permit.reason,
    status: requestStatusLabel(permit.status),
  }));

  const complaints = (apiData.complaints || []).map((c) => ({
    id: c.id,
    title: c.title,
    createdAt: c.createdAt,
    description: c.description,
    status: c.status,
  }));

  const penalties = (apiData.penalties || []).map((p) => ({
    id: p.id,
    type: p.typeLabel || p.type,
    date: p.effectiveDate || p.createdAt,
    effectiveDate: p.effectiveDate,
    days: p.days,
    amount: p.amount,
    reason: formatPenaltyReason(p.reason),
    deductionLabel: formatPenaltyDeduction(p.amount),
    status: p.status,
  }));

  return {
    profile: {
      ...profile,
      id: profile.id,
      name: profile.fullName || profile.name,
      code: profile.employeeCode || profile.code,
      jobTitle: profile.jobTitle,
      joinDate: profile.hireDate || profile.joinDate,
      status: profile.status?.toLowerCase?.() || profile.status,
    },
    requests: [...leaveAsRequests, ...permitAsRequests],
    leaves: apiData.leave || [],
    permits: apiData.permits || [],
    penalties,
    appeals: [],
    complaints,
    resignations: [],
    leaveCalendar: (apiData.attendance?.records || []).map((record) => ({
      date: record.date,
      type: record.status,
      employee: profile.fullName,
    })),
    handovers: [],
    timeline,
    summary: {
      ...(apiData.summary || {}),
      requests:
        leaveAsRequests.length +
        permitAsRequests.length,
      penalties: penalties.length,
      appeals: 0,
      complaints: complaints.length,
      resignations: 0,
      attendanceRecords: apiData.summary?.attendanceRecords ?? 0,
      permitRequests: apiData.summary?.permitRequests ?? permitAsRequests.length,
      leaveRequests: apiData.summary?.leaveRequests ?? leaveAsRequests.length,
      timelineEvents: timeline.length,
    },
    _fromApi: true,
    raw: apiData,
  };
}

const AUDIT_ACTION_LABELS = {
  CREATED: "إنشاء الملف",
  UPDATED: "تحديث الملف",
  TERMINATED: "إنهاء الخدمة",
  REACTIVATED: "إعادة تفعيل الحساب",
  MANAGER_CHANGED: "تغيير المدير",
  STATUS_CHANGED: "تغيير الحالة",
};

function auditActionLabel(action) {
  return AUDIT_ACTION_LABELS[action] || action;
}

function StatusBadge({ status }) {
  if (!status) return null;
  return <span className="eh-status-badge">{status}</span>;
}

function EmptyState({ message }) {
  return <p className="eh-empty">{message}</p>;
}

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function EmployeeHistoryView({
  employee,
  onBack,
  userRole,
  selfView = false,
  initialTab,
}) {
  const { user, isHrLoginAccount } = useAuth();
  const data = useData();
  const [activeTab, setActiveTab] = useState(initialTab || "daily");
  const [yearMonth, setYearMonth] = useState(currentYearMonth);
  const [apiHistory, setApiHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [historyTick, setHistoryTick] = useState(0);
  const [convertDay, setConvertDay] = useState(null);
  const [convertPermitDay, setConvertPermitDay] = useState(null);
  const [convertType, setConvertType] = useState("ANNUAL");
  const [convertReason, setConvertReason] = useState("");
  const [convertError, setConvertError] = useState("");
  const [convertSuccess, setConvertSuccess] = useState("");
  const [converting, setConverting] = useState(false);

  const resolvedEmployee = useMemo(() => {
    if (!employee) return null;
    return (
      data.getEmployeeById(employee.id) ||
      data.teamMembers.find((m) => m.code === employee.code) ||
      employee
    );
  }, [employee, data]);

  const canManageDocuments = useMemo(() => {
    if (selfView) return false;
    const role = userRole || user?.role;
    return role === "admin" || isHrFamilyRole(role);
  }, [userRole, user?.role, selfView]);

  const tabs = useMemo(() => {
    if (!canManageDocuments) return BASE_TABS;
    return [...BASE_TABS, PROFILE_TAB, DOCUMENTS_TAB];
  }, [canManageDocuments]);

  const canView = useMemo(
    () =>
      canViewEmployeeHistory({
        viewerRole: userRole || user?.role,
        viewer: user,
        employee: resolvedEmployee,
        getManagedEmployees: data.getManagedEmployees,
        isHrLoginAccount,
      }),
    [userRole, user, resolvedEmployee, data.getManagedEmployees, isHrLoginAccount],
  );

  const bounds = useMemo(() => monthBounds(yearMonth), [yearMonth]);

  useEffect(() => {
    let cancelled = false;

    async function loadApiHistory() {
      if (!user?._fromBackend || !resolvedEmployee?.id || !isUuid(resolvedEmployee.id)) {
        setApiHistory(null);
        setApiError(null);
        return;
      }

      setLoading(true);
      setApiError(null);

      try {
        const result = await fetchEmployeeHistoryApi(resolvedEmployee.id, {
          from: bounds.from,
          to: bounds.to,
          limit: 366,
        });
        if (!cancelled) {
          setApiHistory(mapApiHistory(result));
        }
      } catch (err) {
        if (!cancelled) {
          setApiHistory(null);
          setApiError(
            err.response?.data?.message || "تعذر تحميل السجل من الخادم",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (canView) {
      loadApiHistory();
    }

    return () => {
      cancelled = true;
    };
  }, [user?._fromBackend, resolvedEmployee?.id, canView, bounds.from, bounds.to, historyTick]);

  const viewerRole = userRole || user?.role;
  const canConvertDays = useMemo(
    () =>
      !selfView &&
      (viewerRole === "admin" ||
        viewerRole === "gm" ||
        isHrFamilyRole(viewerRole)),
    [selfView, viewerRole],
  );

  const derivedHistory = useMemo(
    () => getEmployeeHistory(resolvedEmployee, data),
    [resolvedEmployee, data],
  );

  const history = apiHistory ?? derivedHistory;

  const dailyLedger = useMemo(() => {
    const raw = history?._fromApi ? history.raw : null;
    const attendance = raw?.attendance?.records || [];
    const permits = history?.permits || raw?.permits || [];
    const leaves = history?.leaves || raw?.leave || [];
    const penalties = history?.penalties || [];

    return buildEmployeeDailyLedger({
      attendance,
      permits,
      leaves,
      penalties,
      from: bounds.from,
      to: bounds.to,
    });
  }, [history, bounds.from, bounds.to]);

  const monthOvertimeHours = useMemo(() => {
    const total = dailyLedger.reduce((sum, day) => {
      const hours = Number(day.attendance?.overtimeHours);
      return sum + (Number.isFinite(hours) && hours > 0 ? hours : 0);
    }, 0);
    return Math.round(total * 100) / 100;
  }, [dailyLedger]);

  const terminationLog = useMemo(() => {
    if (
      resolvedEmployee?.status !== "terminated" ||
      !history?._fromApi ||
      !history.raw?.employeeAuditLogs
    ) {
      return null;
    }
    return (
      history.raw.employeeAuditLogs.find((log) => log.action === "TERMINATED") ||
      null
    );
  }, [history, resolvedEmployee?.status]);

  if (!resolvedEmployee) {
    return <EmptyState message="الموظف غير موجود" />;
  }

  if (!canView) {
    return (
      <div className="employee-history" dir="rtl">
        <div className="eh-access-denied">
          <h3>غير مصرح</h3>
          <p>ليس لديك صلاحية عرض سجل هذا الموظف.</p>
          {onBack && (
            <button type="button" className="eh-back-btn" onClick={onBack}>
              ← رجوع
            </button>
          )}
        </div>
      </div>
    );
  }

  const { profile, summary } = history;
  const leaveRemaining = getEmployeeLeaveRemaining(resolvedEmployee);

  function openConvertModal(day) {
    const targets = hrConvertDayTargets(day);
    setConvertDay(day);
    setConvertType(targets[0]?.value || "ANNUAL");
    setConvertReason("");
    setConvertError("");
  }

  function closeConvertModal() {
    if (converting) return;
    setConvertDay(null);
    setConvertError("");
  }

  async function submitConvertDay(event) {
    event.preventDefault();
    if (!convertDay || !resolvedEmployee?.id) return;
    setConvertError("");
    setConvertSuccess("");
    setConverting(true);
    try {
      const result = await convertLeaveDay({
        employeeId: resolvedEmployee.id,
        date: convertDay.date,
        leaveType: convertType,
        reason: convertReason.trim() || undefined,
      });
      const typeLabel =
        hrConvertDayTargets(convertDay).find((item) => item.value === convertType)
          ?.label ||
        HR_CONVERT_LEAVE_TYPES.find((item) => item.value === convertType)
          ?.label ||
        convertType;
      const dateLabel = formatHistoryDate(result?.date || convertDay.date);
      setConvertSuccess(
        convertType === "REST"
          ? `تم تحويل ${dateLabel} إلى راحة أسبوعية`
          : `تم تحويل ${dateLabel} إلى إجازة ${typeLabel}`,
      );
      setConvertDay(null);
      setHistoryTick((tick) => tick + 1);
      await data.refreshData?.();
    } catch (err) {
      setConvertError(
        getApiErrorMessage(err, "تعذر تحويل اليوم"),
      );
    } finally {
      setConverting(false);
    }
  }

  function renderApiExtras() {
    if (!history._fromApi || !history.raw) return null;

    const { employeeAuditLogs = [], attendance, payroll } = history.raw;

    return (
      <>
        {employeeAuditLogs.length > 0 && (
          <div className="eh-api-section">
            <h3 className="eh-api-section-title">سجل تغييرات الملف</h3>
            <table className="eh-table">
              <thead>
                <tr>
                  <th>الإجراء</th>
                  <th>التاريخ</th>
                  <th>بواسطة</th>
                  <th>السبب / الملاحظات</th>
                </tr>
              </thead>
              <tbody>
                {employeeAuditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{auditActionLabel(log.action)}</td>
                    <td>{formatHistoryDate(log.createdAt)}</td>
                    <td>{log.performedBy?.username || log.performedBy?.email || "—"}</td>
                    <td>{log.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {attendance?.records?.length > 0 && activeTab === "timeline" && (
          <div className="eh-api-section">
            <h3 className="eh-api-section-title">سجل الحضور ({attendance.records.length})</h3>
            <table className="eh-table">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الحضور</th>
                  <th>الانصراف</th>
                  <th>الحالة</th>
                  <th>ساعات العمل</th>
                  <th>ساعات زيادة</th>
                  <th>تأخير</th>
                </tr>
              </thead>
              <tbody>
                {attendance.records.slice(0, 31).map((record) => (
                  <tr key={record.id}>
                    <td>{formatHistoryDate(record.date)}</td>
                    <td>{formatHistoryTime(record.checkInTime)}</td>
                    <td>{formatHistoryTime(record.checkOutTime)}</td>
                    <td>{attendanceStatusLabel(record.status)}</td>
                    <td>{record.totalWorkingHours ?? "—"}</td>
                    <td>
                      {Number(record.overtimeHours) > 0
                        ? `${record.overtimeHours}${
                            overtimeDispositionLabel(record.overtimeDisposition)
                              ? ` · ${overtimeDispositionLabel(record.overtimeDisposition)}`
                              : ""
                          }`
                        : "—"}
                    </td>
                    <td>{record.lateMinutes ? `${record.lateMinutes} د` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {payroll?.length > 0 && (
          <div className="eh-api-section">
            <h3 className="eh-api-section-title">سجل الرواتب</h3>
            <table className="eh-table">
              <thead>
                <tr>
                  <th>الشهر</th>
                  <th>الأساسي</th>
                  <th>خصم السلف</th>
                  <th>الصافي</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {payroll.map((record) => (
                  <tr key={record.id}>
                    <td>{record.month}/{record.year}</td>
                    <td>{record.baseSalary}</td>
                    <td>{record.advanceDeductionAmount ?? 0}</td>
                    <td>{record.netSalary}</td>
                    <td>{record.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function renderDailyAttendance() {
    return (
      <div className="eh-daily">
        <div className="eh-daily-toolbar">
          <label className="eh-month-picker">
            <span>الشهر</span>
            <input
              type="month"
              value={yearMonth}
              onChange={(e) => setYearMonth(e.target.value)}
            />
          </label>
          <p className="eh-daily-hint">
            أيام الحضور والانصراف والأذونات والإجازات والجزاءات خلال الشهر المحدد
            {canConvertDays
              ? " — يمكن تحويل يوم الغياب إلى راحة أسبوعية أو إجازة، وتحويل الراحة أو الإجازة إلى اعتيادية / عارضة / مرضية / على الحساب الخاص (بحد أقصى 12 يوم في السنة)، وتحويل التأخير أو الانصراف المبكر إلى إذن حسب رصيد الموظف"
              : ""}
          </p>
        </div>
        {convertSuccess ? (
          <div className="eh-convert-alert success">{convertSuccess}</div>
        ) : null}

        {!dailyLedger.length ? (
          <EmptyState message="لا توجد أحداث حضور أو أذونات أو إجازات أو جزاءات في هذا الشهر" />
        ) : (
          <div className="eh-daily-list">
            {dailyLedger.map((day) => {
              const att = day.attendance;
              const showConvert =
                isUuid(resolvedEmployee.id) &&
                canHrConvertLeaveOrAbsenceDay(day, {
                  role: viewerRole,
                  selfView,
                });
              const showPermitConvert =
                isUuid(resolvedEmployee.id) &&
                canHrConvertAttendanceToPermit(day, {
                  role: viewerRole,
                  selfView,
                });
              return (
                <article key={day.date} className="eh-day-card">
                  <header className="eh-day-header">
                    <div>
                      <h3 className="eh-day-date">{formatHistoryDate(day.date)}</h3>
                      {att ? (
                        <span className={`eh-day-status status-${(att.status || "").toLowerCase()}`}>
                          {attendanceStatusLabel(att.status)}
                        </span>
                      ) : (
                        <span className="eh-day-status status-empty">بدون بصمة</span>
                      )}
                    </div>
                    <div className="eh-day-header-actions">
                      {showConvert ? (
                        <button
                          type="button"
                          className="eh-convert-btn"
                          onClick={() => openConvertModal(day)}
                        >
                          تحويل اليوم
                        </button>
                      ) : null}
                      {showPermitConvert ? (
                        <button
                          type="button"
                          className="eh-convert-btn"
                          onClick={() => {
                            setConvertSuccess("");
                            setConvertPermitDay(day);
                          }}
                        >
                          تحويل لإذن
                        </button>
                      ) : null}
                      {att ? (
                        <div className="eh-day-punches">
                          <div className="eh-punch">
                            <span className="eh-punch-label">حضور</span>
                            <strong>{formatHistoryTime(att.checkInTime)}</strong>
                          </div>
                          <div className="eh-punch">
                            <span className="eh-punch-label">انصراف</span>
                            <strong>{formatHistoryTime(att.checkOutTime)}</strong>
                          </div>
                          {att.lateMinutes > 0 && (
                            <div className="eh-punch late">
                              <span className="eh-punch-label">تأخير</span>
                              <strong>{att.lateMinutes} د</strong>
                            </div>
                          )}
                          {att.totalWorkingHours != null && (
                            <div className="eh-punch">
                              <span className="eh-punch-label">ساعات</span>
                              <strong>{att.totalWorkingHours}</strong>
                            </div>
                          )}
                          {Number(att.overtimeHours) > 0 && (
                            <div className="eh-punch overtime">
                              <span className="eh-punch-label">زيادة</span>
                              <strong>{att.overtimeHours}</strong>
                              {overtimeDispositionLabel(att.overtimeDisposition) ? (
                                <span className="eh-punch-note">
                                  {overtimeDispositionLabel(att.overtimeDisposition)}
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </header>

                  {day.leaves.length > 0 && (
                    <section className="eh-day-section">
                      <h4>إجازات</h4>
                      <ul>
                        {day.leaves.map((leave) => (
                          <li key={leave.id}>
                            <strong>{leaveTypeLabel(leave.leaveType)}</strong>
                            {" · "}
                            {formatHistoryDate(leave.startDate)}
                            {leave.endDate && leave.endDate !== leave.startDate
                              ? ` → ${formatHistoryDate(leave.endDate)}`
                              : ""}
                            {" · "}
                            <StatusBadge status={requestStatusLabel(leave.status)} />
                            {leave.reason ? (
                              <div className="eh-day-detail">السبب: {leave.reason}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {day.permits.length > 0 && (
                    <section className="eh-day-section">
                      <h4>أذونات</h4>
                      <ul>
                        {day.permits.map((permit) => (
                          <li key={permit.id}>
                            <strong>
                              {permit.isCompensatory ? "إذن تعويضي" : "إذن"}
                            </strong>
                            {" · "}
                            {permit.startTime || "—"} – {permit.endTime || "—"}
                            {permit.hours != null ? ` (${permit.hours} س)` : ""}
                            {" · "}
                            <StatusBadge status={requestStatusLabel(permit.status)} />
                            {permit.reason ? (
                              <div className="eh-day-detail">السبب: {permit.reason}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {day.penalties.length > 0 && (
                    <section className="eh-day-section penalties">
                      <h4>جزاءات</h4>
                      <ul>
                        {day.penalties.map((pen) => (
                          <li key={pen.id}>
                            <strong>{pen.type || pen.typeLabel}</strong>
                            {pen.days != null ? ` · ${formatPenaltyDays(pen.days)}` : ""}
                            {pen.deductionLabel ? ` · ${pen.deductionLabel}` : ""}
                            {pen.reason ? (
                              <div className="eh-day-detail">بسبب: {pen.reason}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderTimeline() {
    if (!history.timeline.length) {
      return <EmptyState message="لا توجد أحداث مسجلة لهذا الموظف" />;
    }

    return (
      <div className="eh-timeline">
        {history.timeline.map((event) => (
          <div key={event.id} className={`eh-timeline-item module-${event.module}`}>
            <div className="eh-timeline-dot" />
            <div className="eh-timeline-content">
              <div className="eh-timeline-header">
                <span className="eh-module-tag">{getModuleLabel(event.module)}</span>
                <span className="eh-timeline-date">
                  {formatHistoryDate(event.timestamp)}
                </span>
              </div>
              <div className="eh-timeline-title">
                {auditActionLabel(event.action)}
              </div>
              <div className="eh-timeline-summary">{event.summary}</div>
              {event.action !== "TERMINATED" &&
                event.action !== "REACTIVATED" && (
                <StatusBadge status={event.status} />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderRequests() {
    if (!history.requests.length) {
      return <EmptyState message="لا توجد طلبات" />;
    }
    return (
      <table className="eh-table">
        <thead>
          <tr>
            <th>النوع</th>
            <th>الفترة / التاريخ</th>
            <th>التفاصيل</th>
            <th>الحالة</th>
          </tr>
        </thead>
        <tbody>
          {history.requests.map((req) => (
            <tr key={req.id}>
              <td>{req.type}</td>
              <td>
                {req.startDate
                  ? `${formatHistoryDate(req.startDate)}${
                      req.endDate ? ` → ${formatHistoryDate(req.endDate)}` : ""
                    }`
                  : req.permitDate
                    ? `${formatHistoryDate(req.permitDate)}${
                        req.startTime ? ` · ${req.startTime}–${req.endTime || "—"}` : ""
                      }`
                    : formatHistoryDate(req.submittedAt)}
              </td>
              <td>{req.reason || "—"}</td>
              <td>
                <StatusBadge status={req.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderPenalties() {
    if (!history.penalties.length) {
      return <EmptyState message="لا توجد جزاءات" />;
    }
    return (
      <table className="eh-table">
        <thead>
          <tr>
            <th>النوع</th>
            <th>التاريخ</th>
            <th>الأيام</th>
            <th>الخصم</th>
            <th>السبب</th>
            <th>الحالة</th>
          </tr>
        </thead>
        <tbody>
          {history.penalties.map((pen) => (
            <tr key={pen.id}>
              <td>{pen.type}</td>
              <td>{pen.date}</td>
              <td>{formatPenaltyDays(pen.days)}</td>
              <td>{pen.deductionLabel || "—"}</td>
              <td>{pen.reason}</td>
              <td>
                <StatusBadge
                  status={
                    pen.status === "appealed"
                      ? pen.appealStatus || "معلق"
                      : "ساري"
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderAppeals() {
    if (!history.appeals.length) {
      return <EmptyState message="لا توجد تظلمات" />;
    }
    return (
      <table className="eh-table">
        <thead>
          <tr>
            <th>النوع</th>
            <th>تفاصيل الواقعة</th>
            <th>التاريخ</th>
            <th>سبب التظلم</th>
            <th>المستوى الحالي</th>
            <th>الحالة</th>
          </tr>
        </thead>
        <tbody>
          {history.appeals.map((appeal) => (
            <tr key={appeal.id}>
              <td>{appeal.type}</td>
              <td>
                <AppealSubjectDetails
                  appeal={appeal}
                  penalties={history.penalties}
                  variant="compact"
                />
              </td>
              <td>{formatHistoryDate(appeal.submittedAt)}</td>
              <td>{appeal.reason}</td>
              <td>{getAppealLevelLabel(appeal.currentLevel, appeal) || appeal.currentLevel}</td>
              <td>
                <StatusBadge status={appeal.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderComplaints() {
    if (!history.complaints.length) {
      return <EmptyState message="لا توجد شكاوى" />;
    }
    return (
      <table className="eh-table">
        <thead>
          <tr>
            <th>العنوان</th>
            <th>التاريخ</th>
            <th>الوصف</th>
            <th>الحالة</th>
          </tr>
        </thead>
        <tbody>
          {history.complaints.map((c) => (
            <tr key={c.id}>
              <td>{c.title}</td>
              <td>{formatHistoryDate(c.createdAt)}</td>
              <td>{c.description}</td>
              <td>
                <StatusBadge status={c.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderResignations() {
    if (!history.resignations.length) {
      return <EmptyState message="لا توجد طلبات استقالة" />;
    }
    return (
      <table className="eh-table">
        <thead>
          <tr>
            <th>التاريخ</th>
            <th>السبب</th>
            <th>الحالة</th>
          </tr>
        </thead>
        <tbody>
          {history.resignations.map((r) => (
            <tr key={r.id}>
              <td>{formatHistoryDate(r.submittedAt || r.createdAt)}</td>
              <td>{r.reason || "—"}</td>
              <td>
                <StatusBadge status={r.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderDocuments() {
    if (!canManageDocuments) {
      return <EmptyState message="ليس لديك صلاحية عرض مستندات الموظف" />;
    }
    if (!resolvedEmployee?.id || !isUuid(resolvedEmployee.id)) {
      return <EmptyState message="تعذر تحديد معرّف الموظف لعرض المستندات" />;
    }
    return <EmployeeDocumentsPanel employeeId={resolvedEmployee.id} />;
  }

  function renderProfile() {
    const source = {
      ...resolvedEmployee,
      ...(history.profile || {}),
      name: resolvedEmployee?.name || history.profile?.fullName,
    };
    const completeness =
      source.profileCompleteness || history.profile?.profileCompleteness;
    const { percent } = fieldCompleteness(completeness);
    const missing = formatProfileMissingSummary(completeness);
    return (
      <div className="eh-profile-tab">
        <div className={`hpc-bar hpc-bar--${completenessTone(percent)}`}>
          <span>اكتمال البيانات الشخصية {percent}%</span>
          <div className="hpc-bar-track">
            <div className="hpc-bar-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
        {missing.length > 0 ? (
          <p className="eh-api-notice">ناقص: {missing.join(" · ")}</p>
        ) : (
          <p className="eh-api-notice success">الملف مكتمل</p>
        )}
        <ProfileReadOnlyFields profile={source} />
      </div>
    );
  }

  function renderTabContent() {
    switch (activeTab) {
      case "daily":
        return renderDailyAttendance();
      case "requests":
        return renderRequests();
      case "penalties":
        return renderPenalties();
      case "agenda":
        return (
          <EmployeeAgenda
            employeeId={resolvedEmployee?.id}
            employeeName={profile?.name || resolvedEmployee?.name || ""}
            userRole={userRole}
            compact
            readOnly={selfView}
          />
        );
      case "appeals":
        return renderAppeals();
      case "complaints":
        return renderComplaints();
      case "resignations":
        return renderResignations();
      case "documents":
        return renderDocuments();
      case "profile":
        return renderProfile();
      default:
        return renderTimeline();
    }
  }

  return (
    <div className="employee-history" dir="rtl">
      {!selfView && (
        <div className="eh-header">
          {onBack && (
            <button type="button" className="eh-back-btn" onClick={onBack}>
              ← رجوع
            </button>
          )}
          <div className="eh-profile">
            <div className="eh-avatar">{profile.name.charAt(0)}</div>
            <div>
              <h2 className="eh-name">{profile.name}</h2>
              <p className="eh-meta">
                {profile.code} · {profile.jobTitle}
                {profile.joinDate ? ` · انضم ${profile.joinDate}` : ""}
              </p>
            </div>
          </div>
        </div>
      )}

      {terminationLog && (
        <div className="eh-termination-banner">
          <div className="eh-termination-banner-title">منتهي الخدمة</div>
          <div className="eh-termination-banner-meta">
            بتاريخ {formatHistoryDate(terminationLog.createdAt)}
            {terminationLog.performedBy?.username ||
            terminationLog.performedBy?.email
              ? ` · بواسطة ${terminationLog.performedBy?.username || terminationLog.performedBy?.email}`
              : ""}
          </div>
          {terminationLog.notes ? (
            <div className="eh-termination-banner-reason">
              <strong>سبب إنهاء الخدمة:</strong> {terminationLog.notes}
            </div>
          ) : null}
        </div>
      )}

      <div className="eh-summary-grid">
        <div className="eh-summary-card">
          <span className="eh-summary-value">
            {summary.attendanceRecords ?? dailyLedger.filter((d) => d.attendance).length}
          </span>
          <span className="eh-summary-label">أيام حضور</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">{monthOvertimeHours}</span>
          <span className="eh-summary-label">ساعات زيادة</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">
            {summary.permitRequests ?? summary.permits ?? 0}
          </span>
          <span className="eh-summary-label">أذونات</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">
            {summary.leaveRequests ?? summary.requests ?? 0}
          </span>
          <span className="eh-summary-label">إجازات</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">
            <LeaveRemainingValue
              remaining={leaveRemaining.annualRemaining}
              entitlement={leaveRemaining.annualEntitlement}
            />
          </span>
          <span className="eh-summary-label">اعتيادية متبقية</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">
            <LeaveRemainingValue
              remaining={leaveRemaining.casualRemaining}
              entitlement={leaveRemaining.casualEntitlement}
            />
          </span>
          <span className="eh-summary-label">عارضة متبقية</span>
        </div>
        {canViewSickLeaveRemaining(userRole || user?.role) && (
          <div className="eh-summary-card">
            <span className="eh-summary-value">
              <LeaveRemainingValue
                remaining={leaveRemaining.sickRemaining}
                entitlement={leaveRemaining.sickEntitlement}
              />
            </span>
            <span className="eh-summary-label">مرضية متبقية</span>
          </div>
        )}
        <div className="eh-summary-card">
          <span className="eh-summary-value">{summary.penalties ?? 0}</span>
          <span className="eh-summary-label">جزاءات</span>
        </div>
        <div className="eh-summary-card">
          <span className="eh-summary-value">{summary.complaints ?? 0}</span>
          <span className="eh-summary-label">شكاوى</span>
        </div>
      </div>

      {activeTab !== "daily" &&
        activeTab !== "documents" &&
        activeTab !== "profile" &&
        activeTab !== "agenda" && (
        <div className="eh-daily-toolbar eh-month-bar">
          <label className="eh-month-picker">
            <span>تصفية الشهر</span>
            <input
              type="month"
              value={yearMonth}
              onChange={(e) => setYearMonth(e.target.value)}
            />
          </label>
        </div>
      )}

      {loading && <p className="eh-loading">جاري تحميل السجل...</p>}
      {apiError && !apiHistory && (
        <p className="eh-api-notice">⚠ {apiError} — يُعرض السجل المحلي</p>
      )}
      {history._fromApi && (
        <p className="eh-api-notice success">✓ بيانات من الخادم</p>
      )}

      <div className="eh-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`eh-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="eh-content">{renderTabContent()}</div>
      {activeTab !== "documents" && activeTab !== "daily" && renderApiExtras()}

      {convertPermitDay ? (
        <ConvertAttendanceToPermitModal
          employeeId={resolvedEmployee.id}
          date={convertPermitDay.date}
          employeeName={resolvedEmployee.fullName || resolvedEmployee.name}
          onClose={() => setConvertPermitDay(null)}
          onConverted={(result) => {
            const dateLabel = formatHistoryDate(
              result?.date || convertPermitDay.date,
            );
            const hours = (result?.permits || []).reduce(
              (sum, permit) => sum + Number(permit.hours || 0),
              0,
            );
            setConvertSuccess(
              result?.partial
                ? `تم تحويل ${dateLabel} إلى إذن في حدود الرصيد (${hours} ساعة)`
                : `تم تحويل ${dateLabel} إلى إذن (${hours} ساعة)`,
            );
            setConvertPermitDay(null);
            setHistoryTick((tick) => tick + 1);
            data.refreshData?.();
          }}
        />
      ) : null}

      {convertDay ? (
        <div
          className="eh-convert-overlay"
          role="presentation"
          onClick={closeConvertModal}
        >
          <form
            className="eh-convert-modal"
            dir="rtl"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitConvertDay}
          >
            <h3 className="eh-convert-title">تحويل اليوم</h3>
            <p className="eh-convert-subtitle">
              {formatHistoryDate(convertDay.date)}
              {convertDay.leaves?.[0]
                ? ` · الحالي: ${leaveTypeLabel(convertDay.leaves[0].leaveType)}`
                : convertDay.attendance?.status === "ABSENT"
                  ? " · غياب"
                  : convertDay.attendance?.status === "REST"
                    ? " · راحة أسبوعية"
                    : ""}
            </p>
            {convertError ? (
              <div className="eh-convert-alert error">{convertError}</div>
            ) : null}
            <fieldset className="eh-convert-types">
              <legend>التحويل إلى</legend>
              {hrConvertDayTargets(convertDay).map((option) => (
                <label key={option.value} className="eh-convert-type">
                  <input
                    type="radio"
                    name="eh-convert-leave-type"
                    value={option.value}
                    checked={convertType === option.value}
                    onChange={() => setConvertType(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            {convertType === "REST" ? (
              <p className="eh-daily-hint">
                الراحة الأسبوعية بحد أقصى 4 أيام في الأسبوع و4 في الشهر، ما لم تخصّص الموارد البشرية حداً مختلفاً.
              </p>
            ) : null}
            {convertType === "UNPAID" ? (
              <p className="eh-daily-hint">
                على الحساب الخاص بحد أقصى 12 يوم في السنة الميلادية.
              </p>
            ) : null}
            <label className="eh-convert-reason">
              <span>السبب (اختياري)</span>
              <input
                type="text"
                value={convertReason}
                onChange={(e) => setConvertReason(e.target.value)}
                maxLength={500}
                placeholder={
                  convertType === "REST"
                    ? "يظهر في إشعار الموظف"
                    : "يظهر في سجل الإجازة وإشعار الموظف"
                }
              />
            </label>
            <div className="eh-convert-actions">
              <button
                type="button"
                className="eh-convert-cancel"
                onClick={closeConvertModal}
                disabled={converting}
              >
                إلغاء
              </button>
              <button
                type="submit"
                className="eh-convert-submit"
                disabled={converting}
              >
                {converting ? "جاري التحويل..." : "تأكيد التحويل"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default EmployeeHistoryView;
